// Row 103 LoopSequence (`seq`, scheduling call 12, DC-34): the Final Interview Loop scheduled as a sequence on one day.
// On the left, G4a's SectionRail of the loop's rounds in order — each with its status words and a dot in its
// interviewer's hue; on the right, G7's one-day Availability whose bands stack back to back in each interviewer's hue
// (a 3 px left rule, the Kanban rule). Under the grid, the room and travel rows (core Row) with their task chips
// *room · link · travel*: a done task is the validated chip with a check and the word Completed for assistive
// technology, an open one is neutral. Below 760 px of its own width the rail stacks above the grid.

import type { ReactNode } from "react";
import { Chip, Chips, Row, RowName } from "../core";
import { cx } from "../lib/cx";
import { SectionRail, type SectionRailItem } from "../g4a";
import { Availability, type AvailBand, type AvailRow } from "../g7";
import { LOOP_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

/** A round: a rail item whose `hue` (the interviewer's, a --p-* token var) paints its rail dot and its band. */
export type LoopRound = SectionRailItem;

export type LoopTask = "room" | "link" | "travel";

export interface LoopTaskRow {
  key: string;
  /** "room" or "travel": the row's name. */
  kind: "room" | "travel";
  /** The row's detail (the room, the trip): data. */
  detail?: ReactNode | undefined;
  /** The row's task chips and whether each is done. */
  tasks: readonly { task: LoopTask; done: boolean }[];
}

export interface LoopSequenceProps {
  rounds: readonly LoopRound[];
  /** The day's columns (formatted times). */
  columns: readonly ReactNode[];
  /** The candidate's row and the interviewers' rows. */
  rows: readonly AvailRow[];
  /** The rounds' bands, back to back; a band without a hue or label takes its round's hue and title (by `round`, the
   *  round's key), so each band is read with its round's name. */
  bands?: readonly (AvailBand & { round?: string | undefined })[] | undefined;
  tasks?: readonly LoopTaskRow[] | undefined;
  /** The block's heading (default *Final Interview Loop*). */
  title?: string | undefined;
  loading?: boolean | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

const TASK_WORD: Readonly<Record<LoopTask, string>> = { room: LOOP_LABELS.room, link: LOOP_LABELS.link, travel: LOOP_LABELS.travel };

export function LoopSequence({ rounds, columns, rows, bands = [], tasks = [], title = LOOP_LABELS.finalInterviewLoop, loading, can, hs, className }: LoopSequenceProps) {
  if (can === false) return null;
  const shown = rounds.filter((round) => round.can !== false);
  const byKey = new Map(shown.map((round) => [round.key, round] as const));
  const painted: AvailBand[] = bands.map(({ round, ...band }) => {
    const of = round !== undefined ? byKey.get(round) : undefined;
    return { ...band, hue: band.hue ?? of?.hue, label: band.label ?? of?.title };
  });
  return (
    <div className={cx("mc-seq", className)}>
      <h4 className="mc-seq-h">{title}</h4>
      <div className="mc-seq-b">
        <div className="mc-seq-rail">
          <SectionRail label={LOOP_LABELS.sequence} items={shown} />
        </div>
        <Availability columns={columns} rows={rows} bands={painted} loading={loading} hs={hs} className="mc-seq-day" />
      </div>
      {tasks.length > 0 ? (
        <div className="mc-seq-tasks">
          {tasks.map((row) => (
            <Row key={row.key} lead="30%" cols={2}>
              <RowName name={row.kind === "room" ? LOOP_LABELS.room : LOOP_LABELS.travel} sub={row.detail} />
              <Chips>
                {row.tasks.map(({ task, done }) => (
                  <Chip key={task} sm done={done}>{TASK_WORD[task]}</Chip>
                ))}
              </Chips>
            </Row>
          ))}
        </div>
      ) : null}
    </div>
  );
}
